import paper from 'paper';
import { MIN_BOUNDING_BOX_WIDTH, MIN_BOUNDING_BOX_HEIGHT } from './constants';
import { GridLayer } from './grid';
import * as NeuralaMath from '../../../utils/math';

let minBBWidth = MIN_BOUNDING_BOX_WIDTH;
let minBBHeight = MIN_BOUNDING_BOX_HEIGHT;

function ensurePointAdjustedToGrid(point: paper.Point, grid: GridLayer | null = null): paper.Point {
    return grid && grid.visible ? grid.adjustPointToGrid(point) : point.clone();
}

export function setMinBoundingBoxSize(width: number, height: number): void {
    minBBWidth = width;
    minBBHeight = height;
}

export function resetMinBoundingBoxSizeToDefault(): void {
    minBBWidth = MIN_BOUNDING_BOX_WIDTH;
    minBBHeight = MIN_BOUNDING_BOX_HEIGHT;
}

export function scaleSouthEast(
    e: paper.MouseEvent,
    scaledObjectBounds: paper.Rectangle,
    bounds: paper.Rectangle,
    assetScale: { x: number; y: number },
    grid: GridLayer | null = null
): void {
    const point = ensurePointAdjustedToGrid(e.point, grid);
    const newWidth = point.x - scaledObjectBounds.point.x;
    const newHeight = point.y - scaledObjectBounds.point.y;

    if (bounds.contains(point)) {
        if (isWidthValid(newWidth, bounds, assetScale.x)) {
            scaledObjectBounds.width = newWidth;
        }
        if (isHeightValid(newHeight, bounds, assetScale.y)) {
            scaledObjectBounds.height = newHeight;
        }
    }
}

export function scaleSouthWest(
    e: paper.MouseEvent,
    scaledObjectBounds: paper.Rectangle,
    bounds: paper.Rectangle,
    assetScale: { x: number; y: number },
    grid: GridLayer | null = null
): void {
    const point = ensurePointAdjustedToGrid(e.point, grid);
    const newWidth = scaledObjectBounds.bottomRight.x - point.x;
    const newHeight = point.y - scaledObjectBounds.point.y;

    if (bounds.contains(point)) {
        if (isWidthValid(newWidth, bounds, assetScale.x)) {
            scaledObjectBounds.width = newWidth;
            scaledObjectBounds.point.x = point.x;
        }
        if (isHeightValid(newHeight, bounds, assetScale.y)) {
            scaledObjectBounds.height = newHeight;
        }
    }
}

export function scaleNorthEast(
    e: paper.MouseEvent,
    scaledObjectBounds: paper.Rectangle,
    bounds: paper.Rectangle,
    assetScale: { x: number; y: number },
    grid: GridLayer | null = null
): void {
    const point = ensurePointAdjustedToGrid(e.point, grid);
    const newWidth = point.x - scaledObjectBounds.bottomLeft.x;
    const newHeight = scaledObjectBounds.bottomLeft.y - point.y;

    if (bounds.contains(point)) {
        if (isWidthValid(newWidth, bounds, assetScale.x)) {
            scaledObjectBounds.width = newWidth;
        }
        if (isHeightValid(newHeight, bounds, assetScale.y)) {
            scaledObjectBounds.height = newHeight;
            scaledObjectBounds.point.y = point.y;
        }
    }
}

export function scaleNorthWest(
    e: paper.MouseEvent,
    scaledObjectBounds: paper.Rectangle,
    bounds: paper.Rectangle,
    assetScale: { x: number; y: number },
    grid: GridLayer | null = null
): void {
    const point = ensurePointAdjustedToGrid(e.point, grid);
    const newWidth = scaledObjectBounds.bottomRight.x - point.x;
    const newHeight = scaledObjectBounds.bottomRight.y - point.y;

    if (bounds.contains(point)) {
        if (isWidthValid(newWidth, bounds, assetScale.x)) {
            scaledObjectBounds.width = newWidth;
            scaledObjectBounds.point.x = point.x;
        }
        if (isHeightValid(newHeight, bounds, assetScale.y)) {
            scaledObjectBounds.height = newHeight;
            scaledObjectBounds.point.y = point.y;
        }
    }
}

export function scaleNorth(
    e: paper.MouseEvent,
    scaledObjectBounds: paper.Rectangle,
    bounds: paper.Rectangle,
    assetScale: { x: number; y: number },
    grid: GridLayer | null = null
): void {
    const point = grid && grid.visible ? grid.adjustPointToGrid(e.point) : e.point;
    const newHeight = scaledObjectBounds.bottomRight.y - point.y;

    if (bounds.contains(point)) {
        if (isHeightValid(newHeight, bounds, assetScale.y)) {
            scaledObjectBounds.height = newHeight;
            scaledObjectBounds.point.y = point.y;
        }
    }
}

export function scaleEast(
    e: paper.MouseEvent,
    scaledObjectBounds: paper.Rectangle,
    bounds: paper.Rectangle,
    assetScale: { x: number; y: number },
    grid: GridLayer | null = null
): void {
    const point = ensurePointAdjustedToGrid(e.point, grid);
    const newWidth = point.x - scaledObjectBounds.bottomLeft.x;

    if (bounds.contains(point)) {
        if (isWidthValid(newWidth, bounds, assetScale.x)) {
            scaledObjectBounds.width = newWidth;
        }
    }
}

export function scaleSouth(
    e: paper.MouseEvent,
    scaledObjectBounds: paper.Rectangle,
    bounds: paper.Rectangle,
    assetScale: { x: number; y: number },
    grid: GridLayer | null = null
): void {
    const point = ensurePointAdjustedToGrid(e.point, grid);
    const newHeight = point.y - scaledObjectBounds.topLeft.y;

    if (bounds.contains(point)) {
        if (isHeightValid(newHeight, bounds, assetScale.y)) {
            scaledObjectBounds.height = newHeight;
        }
    }
}

export function scaleWest(
    e: paper.MouseEvent,
    scaledObjectBounds: paper.Rectangle,
    bounds: paper.Rectangle,
    assetScale: { x: number; y: number },
    grid: GridLayer | null = null
): void {
    const point = ensurePointAdjustedToGrid(e.point, grid);
    const newWidth = scaledObjectBounds.topRight.x - point.x;

    if (bounds.contains(point)) {
        if (isWidthValid(newWidth, bounds, assetScale.x)) {
            scaledObjectBounds.width = newWidth;
            scaledObjectBounds.point.x = point.x;
        }
    }
}

export function moveObject(
    e: paper.MouseEvent,
    initialObjectBoundsPoint: paper.Point,
    objectBounds: paper.Rectangle,
    assetLayer: paper.Layer,
    grid: GridLayer | null = null
) {
    initialObjectBoundsPoint.x += e.delta.x;
    initialObjectBoundsPoint.y += e.delta.y;

    const adjustedLeftTop = ensurePointAdjustedToGrid(initialObjectBoundsPoint, grid);

    // when you add 0.1 and 0.2 you never get exactly 0.3
    // therefore we must use floor and normalize point
    // For more info about numbers  with floating point see https://docs.python.org/2/tutorial/floatingpoint.html
    // This is applicable for JS and any other language
    // because it is specified in standard https://en.wikipedia.org/wiki/IEEE_754
    const newBottomRight = new paper.Point(
        NeuralaMath.floorWithPrecision(adjustedLeftTop.x + objectBounds.width),
        NeuralaMath.floorWithPrecision(adjustedLeftTop.y + objectBounds.height)
    );

    if (assetLayer.contains(adjustedLeftTop) && assetLayer.contains(newBottomRight)) {
        objectBounds.point = adjustedLeftTop;
    }
}

export function isHeightValid(newHeight: number, bounds: paper.Rectangle, assetScale: number): boolean {
    const normalizedHeight = newHeight / assetScale;
    return normalizedHeight >= minBBHeight || newHeight >= bounds.height;
}

export function isWidthValid(newWidth: number, bounds: paper.Rectangle, assetScale: number): boolean {
    const normalizedWidth = newWidth / assetScale;
    return normalizedWidth >= minBBWidth || newWidth >= bounds.width;
}

export function adjustToMinWidth(width: number, assetScale: { x: number; y: number }, zoom: number): number {
    const minWidth = minBBWidth * assetScale.x * zoom;

    const normalizedWidth = Math.floor(width / zoom / assetScale.x);

    if (normalizedWidth < minBBWidth) {
        return minWidth;
    }
    return width;
}

export function adjustToMinHeight(height: number, assetScale: { x: number; y: number }, zoom: number): number {
    const minHeight = minBBHeight * assetScale.y * zoom;
    const normalizedHeight = Math.floor(height / zoom / assetScale.y);

    if (normalizedHeight < minBBHeight) {
        return minHeight;
    }
    return height;
}



// WEBPACK FOOTER //
// ./src/components/workspace/objects/bounding-box-utils.ts